import React, { FC, useEffect, useMemo, useState } from "react";
import Icon from "components/Elements/Icon/Icon.tsx";
import useSubscribersOnlyOption from "components/Pages/Home/Helpers/useSubOnlyNotification.tsx";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import { useCustomController } from "../useCustomController.js";
import { Toggle } from "components/Elements/Toggle/Toggle.js";
import { useContent } from "contexts/contentContext/contentContext.js";

interface ICollaborationMailFieldProps {
  textKey: string;
  icon: string;
  disableOption?: string;
  getValues: () => any;
  tooltip?: string;
}

interface IOption {
  value: string;
  icon: string;
}

interface ICollaborateOptionsDropdownProps {
  showDropdown: boolean;
  selectedValue: IOption;
  handleChangeValue: (value: IOption) => void;
}

const collaborateOptionsEnum: IOption[] = [
  {
    value: "recipients",
    icon: "envelope-light",
  },
  {
    value: "link",
    icon: "globe",
  },
  {
    value: "organization",
    icon: "users-light",
  },
];

const CollaborateOptionsDropdown: FC<ICollaborateOptionsDropdownProps> = ({
  showDropdown,
  selectedValue,
  handleChangeValue,
}) => {
  const { getTextByKey } = useContent();
  return (
    <ul
      className={`collaborate-dropdown-component${
        showDropdown ? "" : " hide-dropdown"
      }`}
    >
      {collaborateOptionsEnum.map((option) => (
        <li
          className="collaborate-dropdown-item"
          onClick={() => handleChangeValue(option)}
        >
          <div className="icon-container">
            <Icon name={option.icon} gray />
          </div>
          {getTextByKey(`collaborate-mail-${option.value.toLowerCase()}`)}
          <div className="flex-spacer" />
          {option.value === selectedValue.value && (
            <Icon name="check" blue iconClass="selected-option" />
          )}
        </li>
      ))}
    </ul>
  );
};

const CollaborateOptionsComponent: FC<{
  onChange: (value: string | null) => void;
  value: string | null;
}> = ({ onChange, value }) => {
  const { getTextByKey } = useContent();

  const defaultValue = useMemo(() => {
    return collaborateOptionsEnum.find((option) => option.value === value);
  }, []);

  const [selectedValue, setSelectedValue] = useState(
    defaultValue || collaborateOptionsEnum[0]
  );
  const [showDropdown, setShowDropdown] = useState(false);

  const handleChangeValue = (value: IOption) => {
    setSelectedValue(value);
    setShowDropdown(false);
  };

  useEffect(() => {
    onChange(selectedValue.value);
  }, [selectedValue]);

  return (
    <div className="collaborate-options">
      <div className="collaborate-option-title">
        {getTextByKey("collaborate-mail-option-title")}
      </div>
      <div
        className="collaborate-dropdown-selected-value"
        onClick={() => {
          setShowDropdown((prev) => !prev);
        }}
      >
        <div className="icon-container">
          <Icon
            name={selectedValue.icon}
            gray
            iconClass="security-option-icon"
          />
        </div>
        <div className="info-text-container">
          <div className="info-title">
            {getTextByKey(
              `collaborate-mail-${selectedValue.value.toLowerCase()}`
            )}
            <Icon
              name="arrow-down"
              iconClass={`dropdown-icon ${showDropdown ? "rotated" : ""}`}
              gray
            />
          </div>
          <div className="info-text">
            {getTextByKey(
              `collaborate-mail-${selectedValue.value.toLowerCase()}-text`
            )}
          </div>
        </div>
      </div>
      <CollaborateOptionsDropdown
        {...{ showDropdown, selectedValue, handleChangeValue }}
      />
    </div>
  );
};

const CollaborationMailField: FC<ICollaborationMailFieldProps> = (props) => {
  const { getTextByKey, getHtmlByKey } = useContent();
  const { fieldControls, hasError } = useCustomController(props);

  const [enableLimit, setEnableLimit] = useState(!!fieldControls.value);

  const { SubOnlyNotification, handleSetStatus } = useSubscribersOnlyOption({
    callbackFunc: setEnableLimit,
    subcribersOnly: true,
    titleKey: "collaborate-mail-title",
  });

  useEffect(() => {
    if (!enableLimit) fieldControls.onChange(null);
  }, [enableLimit]);

  return (
    <div className="flex-column">
      <div className="security-option column collaborated-field">
        <div className="row">
          <Icon name="collab" gray iconClass="security-option-icon" />
          <div className="security-downloads-limit-text">
            {getTextByKey("collaborate-mail-title")}
          </div>
          <div className="flex-spacer" />
          <SubOnlyNotification />
          <Toggle status={enableLimit} setStatus={handleSetStatus} />
          <Tooltip tooltipText={getHtmlByKey("collaborate-mail-tooltip")}>
            <Icon name="i" gray />
          </Tooltip>
        </div>
      </div>
      {enableLimit ? (
        <CollaborateOptionsComponent
          onChange={fieldControls.onChange}
          value={fieldControls.value}
        />
      ) : null}
    </div>
  );
};

export default CollaborationMailField;
